import React from "react";
import MarkEditor from "./../../../_editor/components/rich_plugins/mark_editor/MarkEditor";
import Mark from "../../../common/components/mark/Mark";
import i18n from "i18next";
import { LuFileText, LuFileX2, LuLoaderCircle } from "react-icons/lu";

// PDF.js v3.x with React 19 compatibility - Direct rendering without react-pdf
let pdflib = null;
let pdfSupported = true;

try {
  pdflib = require("pdfjs-dist");

  // Configure worker for PDF.js v3.x
  if (pdflib.GlobalWorkerOptions) {
    pdflib.GlobalWorkerOptions.workerSrc =
      "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
  }

  console.log("EnrichedPDF editor loaded successfully with pdfjs-dist");
} catch (error) {
  console.error("PDF.js could not be loaded:", error);
  pdfSupported = false;
}
/* eslint-disable react/prop-types */

const STATUS_ICONS = { empty: LuFileText, loading: LuLoaderCircle, error: LuFileX2 };

// Placeholder shown on the canvas while the PDF is loading / missing / broken.
function renderStatus(variant, title, hint) {
  const Icon = STATUS_ICONS[variant];
  return (
    <div className={"pdfStatus pdfStatus--" + variant}>
      <div className="pdfStatus__icon">
        <Icon size={26} />
      </div>
      <div className="pdfStatus__title">{title}</div>
      {hint ? <div className="pdfStatus__hint">{hint}</div> : null}
    </div>
  );
}

export default class EnrichedPDFPluginEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      numPages: null,
      pageNumber: 1,
      pdfDocument: null,
      loading: true,
      error: null,
    };
    this.canvasRef = React.createRef();
    this.buttonBack = this.buttonBack.bind(this);
    this.buttonNext = this.buttonNext.bind(this);
    this.renderPage = this.renderPage.bind(this);
  }

  componentDidMount() {
    this.loadPdf();
  }

  componentDidUpdate(prevProps, prevState) {
    // Reload PDF if URL changes
    if (prevProps.state.url !== this.props.state.url) {
      this.loadPdf();
    }
    // Re-render page if page number changes
    if (prevState.pageNumber !== this.state.pageNumber && this.state.pdfDocument) {
      this.renderPage(this.state.pageNumber);
    }
  }

  async loadPdf() {
    if (!pdflib || !this.props.state.url) {
      this.setState({ loading: false, error: "PDF URL not provided" });
      return;
    }

    try {
      this.setState({ loading: true, error: null });
      const loadingTask = pdflib.getDocument({ url: this.props.state.url, isEvalSupported: false });
      const pdfDocument = await loadingTask.promise;

      this.setState(
        {
          pdfDocument,
          numPages: pdfDocument.numPages,
          loading: false,
        },
        () => {
          this.renderPage(1);
        }
      );
    } catch (error) {
      console.error("Error loading PDF:", error);
      this.setState({
        loading: false,
        error: "Failed to load PDF: " + error.message,
      });
    }
  }

  async renderPage(pageNumber) {
    const { pdfDocument } = this.state;
    if (!pdfDocument || !this.canvasRef.current) return;

    try {
      const page = await pdfDocument.getPage(pageNumber);
      const canvas = this.canvasRef.current;
      const context = canvas.getContext("2d");

      // Calculate scale to fit container
      const container = canvas.parentElement;
      const containerWidth = container ? container.clientWidth : 800;
      const viewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error("Error rendering page:", error);
    }
  }

  buttonNext() {
    if (this.state.pageNumber < this.state.numPages) {
      this.setState({
        pageNumber: this.state.pageNumber + 1,
      });
    }
  }

  buttonBack() {
    if (this.state.pageNumber > 1) {
      this.setState({
        pageNumber: this.state.pageNumber - 1,
      });
    }
  }

  render() {
    // Handle disabled PDF functionality
    if (!pdfSupported) {
      return renderStatus(
        "error",
        i18n.t("EnrichedPDF.unavailable_title"),
        i18n.t("EnrichedPDF.unavailable_hint")
      );
    }

    const { loading, error, numPages, pageNumber } = this.state;

    if (loading) {
      return renderStatus("loading", i18n.t("EnrichedPDF.loading"));
    }

    if (error) {
      return renderStatus(
        "empty",
        i18n.t("EnrichedPDF.empty_title"),
        i18n.t("EnrichedPDF.empty_hint")
      );
    }

    let marks = this.props.props.marks || {};
    let markElements = Object.keys(marks).map((id) => {
      let value = marks[id].value;
      let title = marks[id].title;
      let color = marks[id].color;
      let position;
      if (value && value.split(",").length === 3) {
        position = value.split(",");
      } else {
        position = [0, 0, 0];
      }

      let x = "" + position[0] + "%";
      let y = "" + position[1] + "%";

      let bool = parseFloat(position[2]) === pageNumber;
      return bool ? (
        <MarkEditor
          boxId={this.props.props.id}
          key={id}
          style={{ left: x, top: y, position: "absolute" }}
          time={1.5}
          mark={id}
          onRichMarkMoved={this.props.props.onRichMarkMoved}
          state={this.props.state}
          base={this.props.base}
        >
          <Mark
            style={{ position: "relative", top: "-24px", left: "-10px" }}
            color={color || "#eab700"}
            idKey={id}
            title={title}
          />
        </MarkEditor>
      ) : null;
    });

    return (
      <div style={{ width: "100%", height: "100%" }} className={"pdfDiv"}>
        <div className="topBar editorTopBar">
          <button className={"PDFback"} onClick={this.buttonBack}>
            <i className={"material-icons"}>keyboard_arrow_left</i>
          </button>
          <span className={"PDFnumPages"}>
            {pageNumber} of {numPages || "?"}
          </span>
          <button className={"PDFnext"} onClick={this.buttonNext}>
            <i className={"material-icons"}>keyboard_arrow_right</i>
          </button>
        </div>
        <div className="dropableRichZone" style={{ position: "relative", width: "100%" }}>
          <canvas ref={this.canvasRef} className="pdfPage" style={{ width: "100%" }} />
          {markElements}
        </div>
      </div>
    );
  }
}
/* eslint-enable react/prop-types */
